import { apiService } from "../api.service";
import {
	FieldInfoApiError,
	type FieldInfoData,
	FieldInfoDataArraySchema,
	FieldInfoLabelSchema,
	type FieldInfoLocalizationsParams,
	type FieldInfoOrderBys,
	FieldInfoOrderBysSchema,
	type FieldInfoParams,
} from "./field-info.types";

export class FieldInfoService {
	/**
	 * Get field infos for an entity type
	 * GET /field-infos/{entityType}
	 */
	async getFieldInfos(
		entityType: string,
		params: FieldInfoParams = {},
	): Promise<FieldInfoData[]> {
		try {
			const response = await apiService.get<FieldInfoData[]>(
				`/field-infos/${encodeURIComponent(entityType)}`,
				{ params },
			);
			return FieldInfoDataArraySchema.parse(response);
		} catch (error) {
			if (error instanceof FieldInfoApiError) {
				throw error;
			}
			throw new FieldInfoApiError(
				`Failed to get field infos for ${entityType}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get display label for an entity type
	 * GET /field-infos/{entityType}/label
	 */
	async getFieldInfosLabel(
		entityType: string,
		params: FieldInfoParams = {},
	): Promise<string> {
		try {
			const response = await apiService.get<string>(
				`/field-infos/${encodeURIComponent(entityType)}/label`,
				{ params },
			);
			return FieldInfoLabelSchema.parse(response);
		} catch (error) {
			if (error instanceof FieldInfoApiError) {
				throw error;
			}
			throw new FieldInfoApiError(
				`Failed to get field info labels for ${entityType}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get field info localizations
	 * GET /field-infos/localizations
	 */
	async getFieldInfosLocalizations(
		params: FieldInfoLocalizationsParams = {},
	): Promise<string> {
		try {
			const response = await apiService.get<string | Record<string, unknown>>(
				"/field-infos/localizations",
				{ params },
			);
			return typeof response === "string" ? response : JSON.stringify(response);
		} catch (error) {
			if (error instanceof FieldInfoApiError) {
				throw error;
			}
			throw new FieldInfoApiError(
				`Failed to get field info localizations: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get order-by options for an entity type
	 * GET /field-infos/{entityType}/order-bys
	 */
	async getFieldInfosOrderBys(entityType: string): Promise<FieldInfoOrderBys> {
		try {
			const response = await apiService.get<string[]>(
				`/field-infos/${encodeURIComponent(entityType)}/order-bys`,
			);
			return FieldInfoOrderBysSchema.parse(response);
		} catch (error) {
			if (error instanceof FieldInfoApiError) {
				throw error;
			}
			throw new FieldInfoApiError(
				`Failed to get field info order-bys for ${entityType}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

export const fieldInfoService = new FieldInfoService();
